import {HttpErrorResponse} from '@angular/common/http';
import {Component, OnInit} from '@angular/core';
import type {UserActions, UserEntity} from '@gatewee/components/v2';
import {RecipientUser} from '@gatewee/types/user';
import {TranslateService} from '@ngx-translate/core';
import {TuiAlertService} from '@taiga-ui/core';
import {
  isTwoFactorCancelled,
  isTwoFactorMaxAttempts,
} from '@gatewee/services/two-factor-auth.service';
import {throwError} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {UsersService} from '../../../app/_services/users/users.service';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.css'],
  standalone: false,
})
export class UsersComponent implements OnInit {
  from = 0;
  take = 10;
  page = 1;
  pageSize = 10;
  users: RecipientUser[] = [];

  breadcrumb = [
    {label: 'nav.breadcrumb.settings', path: '/panel'},
    {label: 'nav.menu.users'},
  ];

  userActions: UserActions = {
    onInvite: (email: string, role: string) =>
      this.usersService
        .createWith2Fa({email, role} as unknown as RecipientUser)
        .pipe(
          tap(() => {
            this.fetchUsers();
            this.alerts
              .open(
                this.translate.instant(
                  'features.users.swalMessages.texts.inviteSuccess',
                ),
                {
                  label: this.translate.instant(
                    'features.users.swalMessages.success',
                  ),
                  appearance: 'success',
                },
              )
              .subscribe();
          }),
          catchError((err: unknown) => {
            if (isTwoFactorCancelled(err)) {
              return throwError(() => err);
            }
            if (isTwoFactorMaxAttempts(err)) {
              this.alerts
                .open(
                  this.translate.instant(
                    'common.twoFactor.maxAttemptsExceeded',
                  ),
                  {
                    label: this.translate.instant(
                      'features.users.swalMessages.error',
                    ),
                    appearance: 'error',
                  },
                )
                .subscribe();
              return throwError(() => err);
            }
            this.alerts
              .open(
                (err as HttpErrorResponse)?.error?.message ??
                  this.translate.instant(
                    'features.users.swalMessages.texts.errorMessage',
                  ),
                {
                  label: this.translate.instant(
                    'features.users.swalMessages.error',
                  ),
                  appearance: 'error',
                },
              )
              .subscribe();
            return throwError(() => err);
          }),
          map(() => undefined),
        ),
    onResendInvitation: (user: UserEntity) =>
      this.usersService.resendInvitation((user as RecipientUser).id).pipe(
        tap(() =>
          this.alerts
            .open(
              this.translate.instant(
                'features.users.swalMessages.texts.resendInvite',
              ),
              {
                label: this.translate.instant(
                  'features.users.swalMessages.success',
                ),
                appearance: 'success',
              },
            )
            .subscribe(),
        ),
        catchError((err: HttpErrorResponse) => {
          this.alerts
            .open(
              err?.error?.message ??
                this.translate.instant(
                  'features.users.swalMessages.texts.errorMessage',
                ),
              {
                label: this.translate.instant(
                  'features.users.swalMessages.error',
                ),
                appearance: 'error',
              },
            )
            .subscribe();
          return throwError(() => err);
        }),
        map(() => undefined),
      ),
  };

  constructor(
    private readonly translate: TranslateService,
    private readonly usersService: UsersService,
    private readonly alerts: TuiAlertService,
  ) {}

  ngOnInit(): void {
    this.fetchUsers();
  }

  fetchUsers(): void {
    this.usersService.findAll().subscribe({
      next: (data: RecipientUser[]) => {
        this.users = data;
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error fetching users:', error);
      },
    });
  }

  onPageChange(page: number): void {
    this.page = page;
    this.from = (page - 1) * this.take;
    this.fetchUsers();
  }
}
